import type { CourseInstanceDetails, User } from 'src/types';

export type CourseRouteContext = {
  user: User;
  isLecturer: boolean;
  isStudent: boolean;
  lessonsCount: number;
  studentCount: number;
  can: (action: string, subject: any) => boolean;
};

export type CourseRouteMetaCondition = (
  course: CourseInstanceDetails,
  context: CourseRouteContext
) => boolean;

export type CourseRouteMeta = {
  title:
    | string
    | ((course: CourseInstanceDetails, context: CourseRouteContext) => string);
  condition?: CourseRouteMetaCondition;
};

export const courseRoute = {
  path: 'courses/:id',
  redirect: { name: 'showCourse' },
  component: () => import('src/layouts/CourseLayout.vue'),
  children: [
    {
      name: 'showCourse',
      path: '',
      redirect: { name: 'showCourseDashboard' },
    },
    {
      name: 'showCourseDashboard',
      path: 'dashboard',
      meta: { title: 'Dashboard' },
      redirect: { name: 'showDashboardInfo' },
      component: () => import('src/pages/courses/CourseDashboard.vue'),
      children: [
        {
          name: 'showDashboardInfo',
          path: 'info',
          component: () => import('src/pages/courses/dashboard/CourseInfo.vue'),
        },
        {
          name: 'showDashboardCourseCalendar',
          path: 'calendar',
          component: () =>
            import('src/pages/courses/dashboard/CourseCalendar.vue'),
        },
      ],
    },
    {
      name: 'showCourseClassMaterial',
      path: 'material',
      meta: {
        title: () => 'Class Feed',
        condition: (course: CourseInstanceDetails) => course.v2,
      } as CourseRouteMeta,
      component: () => import('src/pages/courses/ClassMaterial.vue'),
    },
    {
      path: 'activity',
      name: 'showCourseActivity',
      meta: {
        title: (_, { lessonsCount }) => `Sessions (${lessonsCount})`,
      } as CourseRouteMeta,
      redirect: { name: 'showDashboardCourseActivity' },
      component: () => import('pages/courses/activity/LessonPage.vue'),
      children: [
        {
          path: '',
          name: 'showDashboardCourseActivity',
          component: () => import('src/pages/courses/activity/LessonList.vue'),
          children: [
            {
              path: 'lessons/:lessonId',
              name: 'showCourseLesson',
              redirect: { name: 'showCourseLessonPlan' },
              component: () =>
                import('pages/courses/activity/LessonDetails.vue'),
              children: [
                {
                  path: 'plan',
                  name: 'showCourseLessonPlan',
                  component: () =>
                    import('src/pages/courses/activity/LessonPlan.vue'),
                },
                {
                  path: 'attendance',
                  name: 'showCourseLessonAttendance',
                  component: () =>
                    import('src/pages/courses/activity/LessonAttendance.vue'),
                },
                {
                  path: 'summary',
                  name: 'showCourseLessonSummary',
                  component: () =>
                    import('src/pages/courses/activity/LessonSummary.vue'),
                },
              ],
            },
          ],
        },
        {
          path: 'manage_plan',
          name: 'showLessonsManagePlan',
          component: () =>
            import('src/pages/courses/activity/LessonManagePlan.vue'),
        },
        {
          path: 'manage_session',
          name: 'showLessonsManageSession',
          component: () =>
            import('src/pages/courses/activity/LessonManageSession.vue'),
        },
      ],
    },
    {
      name: 'showExamPaper',
      path: 'exam_paper',
      meta: {
        title: () => 'Exam Paper',
        condition: (course) => course.type === 'EXAM',
      } as CourseRouteMeta,
      component: () => import('src/pages/courses/CourseExamPaper.vue'),
    },
    {
      name: 'showCourseSyllabus',
      path: 'syllabus',
      meta: {
        title: () => 'Syllabus',
        condition: (course) => course.type !== 'EXAM' && course.v2,
      } as CourseRouteMeta,
      component: () => import('src/pages/courses/CourseSyllabus.vue'),
      children: [
        {
          name: 'showCourseSyllabusEdit',
          path: 'edit',
          component: () => import('src/pages/courses/syllabus/EditPage.vue'),
        },
        {
          name: 'showCourseSyllabusView',
          path: 'view',
          component: () => import('src/pages/courses/syllabus/ViewPage.vue'),
        },
      ],
    },
    {
      name: 'showCourseV1Syllabus',
      path: 'syllabus_v1',
      meta: {
        title: () => 'Syllabus',
        condition: (course) => !course.v2,
      } as CourseRouteMeta,
      component: () => import('src/pages/courses/V1Syllabus.vue'),
    },
    {
      path: 'students',
      name: 'showCourseStudents',
      meta: {
        title: (_, { studentCount }) => `Students (${studentCount})`,
        condition: (_, { isLecturer }) => isLecturer,
      } as CourseRouteMeta,
      component: () => import('src/pages/courses/student/StudentList.vue'),
      children: [
        {
          path: ':enrollmentId',
          name: 'showStudent',
          redirect: { name: 'studentAttendance' },
          component: () => import('src/pages/courses/student/StudentShow.vue'),
          children: [
            {
              path: 'attendance',
              name: 'studentAttendance',
              component: () =>
                import('src/pages/courses/student/StudentAttendance.vue'),
            },
            {
              path: 'schedule',
              name: 'studentSchedule',
              component: () =>
                import('src/pages/courses/student/StudentSchedule.vue'),
            },
            {
              path: 'study_plan/:userProgramId',
              name: 'studentProgram',
              component: () =>
                import('src/pages/courses/student/StudentProgram.vue'),
            },
          ],
        },
      ],
    },
    {
      name: 'showCourseAssessments',
      path: 'assessments',
      redirect: { name: 'showInputMark' },
      meta: {
        title: () => 'Assessments',
        condition: (_: unknown, context: { isLecturer: boolean }) =>
          context.isLecturer,
      },
      component: () =>
        import('src/pages/courses/assessment/AssessmentPage.vue'),
      children: [
        {
          path: 'input_mark',
          name: 'showInputMark',
          component: () => import('src/pages/courses/assessment/InputMark.vue'),
        },
        {
          path: 'guest_grading',
          name: 'showGuestGrading',
          component: () =>
            import('src/pages/courses/assessment/GuestGrading.vue'),
        },
        {
          path: 'assessment',
          name: 'showManageAssessment',
          component: () =>
            import('src/pages/courses/assessment/ManageAssessment.vue'),
          children: [
            {
              path: 'create',
              name: 'addAssessment',
              component: () =>
                import('src/pages/courses/assessment/AddAssessment.vue'),
            },
            {
              path: ':assessmentId',
              name: 'showAssessment',
              component: () =>
                import('src/pages/courses/assessment/ShowAssessment.vue'),
            },
          ],
        },
      ],
    },

    {
      name: 'showCourseV1Syllabus',
      path: 'v1_syllabus',
      meta: {
        title: 'Syllabus',
        condition: (course: CourseInstanceDetails) => !course.v2,
      } as CourseRouteMeta,
      component: () => import('src/pages/courses/V1Syllabus.vue'),
    },
    {
      path: 'grading',
      name: 'showCourseGrading',
      meta: { title: 'Grading' } as CourseRouteMeta,
      redirect: { name: 'showCourseGradingFinal' },
      component: () => import('src/pages/courses/grading/GradingPage.vue'),
      children: [
        {
          path: '',
          redirect: { name: 'showCourseGradingFinal' },
        },
        // {
        //   name: 'showCourseGradingBreakdown',
        //   path: 'breakdown',
        //   component: () =>
        //     import('src/pages/courses/grading/breakdown/GradingBreakdown.vue'),
        //   children: [
        //     {
        //       name: 'showDetailAssessmentBreakdown',
        //       path: ':assessmentId',
        //       component: () =>
        //         import(
        //           'src/pages/courses/grading/breakdown/BreakdownDetails.vue'
        //         ),
        //     },
        //   ],
        // },
        {
          name: 'showCourseGradingFinal',
          path: 'final',
          component: () => import('src/pages/courses/grading/GradingFinal.vue'),
        },
        {
          name: 'showCourseGradingReport',
          path: 'report',
          component: () =>
            import('src/pages/courses/grading/GradingReportPage.vue'),
        },
        {
          name: 'showCourseGradingSample',
          path: 'sample',
          component: () =>
            import('src/pages/courses/grading/GradingSample.vue'),
        },
        {
          name: 'showCourseGradingSubmit',
          path: 'submit',
          component: () =>
            import('src/pages/courses/grading/GradingSubmit.vue'),
        },
      ],
    },
    {
      name: 'showCourseClassRecord',
      path: 'record',
      component: () => import('src/pages/courses/ClassRecord.vue'),
    },
    {
      name: 'showCourseSrti',
      path: 'srti',
      meta: {
        title: () => 'Feedback',
        condition: (course, context) =>
          course.showFeedback || context.can('view-srti', course) || context.isLecturer,
      } as CourseRouteMeta,
      component: () => import('src/pages/courses/CourseSrti.vue'),
    },

    {
      name: 'showCourseSetting',
      path: 'setting',
      redirect: { name: 'copyCourse' },
      component: () => import('src/pages/courses/setting/SettingPage.vue'),
      children: [
        {
          name: 'copyCourse',
          path: 'copy',
          component: () => import('src/pages/courses/setting/CopySetting.vue'),
        },
        {
          name: 'shareCode',
          path: 'share',
          component: () => import('src/pages/courses/setting/ShareSetting.vue'),
        },
        {
          name: 'googleClassroom',
          path: 'google_classroom',
          component: () =>
            import('src/pages/courses/setting/GoogleClassroomSetting.vue'),
        },
        {
          name: 'courseMoodle',
          path: 'course_moodle',
          component: () =>
            import('src/pages/courses/setting/MoodleSetting.vue'),
        },
        {
          name: 'courseHub',
          path: 'hub',
          component: () => import('src/pages/courses/setting/HubSetting.vue'),
        },
      ],
    },
  ],
};
